import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/research", label: "Research" },
  { to: "/practice", label: "Practice" },
  { to: "/community", label: "Community" },
  { to: "/about", label: "About" },
];

const COMMUNITY_SUB = [
  { to: "/research-dialogues", label: "Research Dialogues" },
  { to: "/roundtables", label: "Roundtables" },
  { to: "/reading-circle", label: "Research Circle" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled || open ? "border-b border-line bg-white backdrop-blur-xl" : "border-b border-transparent bg-white"}`}>
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 font-serif text-2xl md:text-3xl tracking-tight font-medium">
          <img src="/images/logo.png" alt="Mentheris logo" className="h-8 md:h-10 w-auto" />
          <span>Mentheris<span className="text-ember">.</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="nav-desktop">
          {LINKS.map((l) =>
            l.label === "Community" ? (
              <div key={l.to} className="relative group">
                <NavLink
                  to={l.to}
                  data-testid="nav-link-community"
                  className={({ isActive }) =>
                    `link-editorial text-sm tracking-wide transition-colors duration-300 ${
                      isActive ? "is-active text-ink font-medium" : "text-fog hover:text-ink"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 translate-y-1 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:translate-y-0 focus-within:pointer-events-auto transition-all duration-300"
                  data-testid="nav-community-submenu"
                >
                  <div className="flex flex-col gap-3 whitespace-nowrap bg-bone/95 backdrop-blur-md px-4 py-4">
                    {COMMUNITY_SUB.map((s) => (
                      <NavLink
                        key={s.to}
                        to={s.to}
                        data-testid={`nav-sub-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="group/s flex items-center gap-2.5 text-[13px] tracking-wide text-fog hover:text-ink transition-colors duration-300"
                      >
                        <span className="w-1 h-1 rounded-full bg-leaf opacity-0 group-hover/s:opacity-100 transition-opacity duration-300" aria-hidden="true" />
                        {s.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-editorial text-sm tracking-wide transition-colors duration-300 ${
                    isActive ? "is-active text-ink font-medium" : "text-fog hover:text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <a
            href="mailto:contact@mentheris.com"
            data-testid="nav-get-in-touch"
            className="border border-ink px-5 py-2.5 text-sm tracking-wide hover:bg-ink hover:text-bone transition-colors duration-300"
          >
            Get in touch
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden border-t border-line bg-bone"
            data-testid="nav-mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <div key={l.to}>
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className="font-serif text-2xl"
                  >
                    {l.label}
                  </NavLink>
                  {l.label === "Community" && (
                    <div className="mt-3 ml-1 flex flex-col gap-2.5 border-l border-line pl-4" data-testid="nav-mobile-community-sub">
                      {COMMUNITY_SUB.map((s) => (
                        <NavLink
                          key={s.to}
                          to={s.to}
                          onClick={() => setOpen(false)}
                          data-testid={`nav-mobile-sub-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="text-sm text-fog hover:text-ink transition-colors duration-300"
                        >
                          {s.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a
                href="mailto:contact@mentheris.com"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-get-in-touch"
                className="mt-2 inline-flex w-fit items-center border border-ink px-6 py-3 text-sm tracking-wide"
              >
                Get in touch
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
